/**
 * ConfigHistorySection — List of config snapshots (versioned environments).
 * Click on a version to see the full config at that point in time.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { getConfigSnapshots, getConfigSnapshotDetail } from '@/services/credit-engine.service';
import { SnapshotConfigView } from '../SnapshotConfigView';
import { PARTNER_ID } from '@/constants';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

// ─── DynamoDB unmarshalling ──────────────────────────────────────────────────
// Snapshot config values come as JSON strings in DynamoDB raw format
// e.g. {"N":"55"} → 55, {"S":"text"} → "text", {"L":[...]} → [...]

function unmarshallValue(val: any): any {
  if (val == null) return val;
  if (typeof val !== 'object') return val;

  // DynamoDB type descriptors
  if ('S' in val) return val.S;
  if ('N' in val) return Number(val.N);
  if ('BOOL' in val) return val.BOOL;
  if ('NULL' in val) return null;
  if ('L' in val) return (val.L as any[]).map(unmarshallValue);
  if ('M' in val) {
    const result: Record<string, any> = {};
    for (const [k, v] of Object.entries(val.M)) result[k] = unmarshallValue(v);
    return result;
  }

  // Not a DynamoDB-typed value — recurse into plain object/array
  if (Array.isArray(val)) return val.map(unmarshallValue);

  // Check if this is a plain object with DynamoDB-typed values (item-level)
  const keys = Object.keys(val);
  if (keys.length > 0 && keys.some(k => typeof val[k] === 'object' && val[k] !== null && ('S' in val[k] || 'N' in val[k] || 'BOOL' in val[k] || 'L' in val[k] || 'M' in val[k]))) {
    const result: Record<string, any> = {};
    for (const [k, v] of Object.entries(val)) result[k] = unmarshallValue(v);
    return result;
  }

  return val;
}

/** Parse snapshot config: JSON-string values → parsed → DynamoDB unmarshalled */
function resolveSnapshotConfig(rawConfig: Record<string, any>): Record<string, any> {
  const resolved: Record<string, any> = {};
  for (const [key, val] of Object.entries(rawConfig)) {
    let parsed = val;
    // Step 1: parse JSON strings
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { /* keep as-is */ }
    }
    // Step 2: unmarshall DynamoDB format
    parsed = unmarshallValue(parsed);
    resolved[key] = parsed;
  }
  return resolved;
}

const CONFIG_TYPE_LABELS: Record<string, string> = {
  'eligibility-rules': 'Elegibilidad',
  'credit-levels': 'Niveles',
  'interest-rates': 'Tasas',
  'split': 'Split',
  'exposure-limits': 'Limites',
  'scores': 'Scoring',
  'bank-accounts': 'Cuentas',
  'operational-params': 'Parametros',
  'baseline': 'Baseline',
};

interface ConfigHistorySectionProps {
  filterTypes?: string[] | null;
}

export function ConfigHistorySection({ filterTypes }: ConfigHistorySectionProps) {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const { data: snapshotsData, isLoading } = useQuery({
    queryKey: ['config', 'snapshots'],
    queryFn: () => getConfigSnapshots(PARTNER_ID),
  });

  const { data: detailData, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['config', 'snapshot', selectedVersion],
    queryFn: () => selectedVersion ? getConfigSnapshotDetail(PARTNER_ID, selectedVersion) : Promise.resolve(null),
    enabled: !!selectedVersion && selectedVersion > 0,
  });

  const allSnapshots: any[] = snapshotsData?.data ?? [];
  const realSnapshots = filterTypes
    ? allSnapshots.filter((s: any) => filterTypes.includes(s.config_type_changed))
    : allSnapshots;

  // Mock baseline for operational types when no real snapshots exist
  const OPS_FILTER = ['split', 'bank-accounts', 'operational-params'];
  const isOpsFilter = filterTypes != null && filterTypes.every((t) => OPS_FILTER.includes(t));
  const MOCK_BASELINE = {
    version: 0,
    created_at: '2026-04-01T00:00:00.000Z',
    created_by: 'sistema',
    config_type_changed: 'baseline',
    description: 'Configuracion inicial (baseline)',
    _isMock: true,
  };
  const snapshots = realSnapshots.length === 0 && isOpsFilter
    ? [MOCK_BASELINE]
    : realSnapshots;

  const rawDetail = detailData?.data ?? null;
  const isMockSelected = selectedVersion === 0 && isOpsFilter;

  // Resolve the config: values arrive as JSON strings in DynamoDB raw format.
  // Parse + unmarshall them into clean JS objects.
  const detail = (() => {
    // Mock baseline detail
    if (isMockSelected) {
      return {
        version: 0,
        created_at: '2026-04-01T00:00:00.000Z',
        created_by: 'sistema',
        config_type_changed: 'baseline',
        description: 'Configuracion inicial (baseline). El historial real se creara automaticamente cuando el backend soporte snapshots operativos.',
        config: {
          split: { fiado_rate_points: 5, partner_rate_points: 55, iva_rate: 0.16 },
          bank_accounts: {
            fiado: { clabe: '012180015000000001', bank_name: 'BBVA Mexico' },
            partner: { clabe: '014180025000000002', bank_name: 'Santander' },
          },
          operational_params: {
            weeks_to_default: 8,
            income_window_days: 90,
            min_deposit_amount: 1.0,
            frequency_ranges: { DAILY: '0-4', WEEKLY: '5-11', BIWEEKLY: '12-24', MONTHLY: '25-35' },
            min_deposits: { DAILY: 4, WEEKLY: 4, BIWEEKLY: 3, MONTHLY: 2 },
          },
        },
      };
    }

    if (!rawDetail) return null;

    let config = rawDetail.config;
    if (!config || typeof config !== 'object') return rawDetail;

    // Parse JSON-string values and unmarshall DynamoDB format
    config = resolveSnapshotConfig(config);

    if (import.meta.env.DEV) {
      console.log('[ConfigHistory] resolved config:', config);
    }

    return { ...rawDetail, config };
  })();

  if (isLoading) return <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>;
  if (snapshots.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">Sin historial de cambios. Los snapshots se crean automaticamente al modificar la configuracion.</p>;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
      {/* List */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="border-b border-border bg-muted/30 px-3 py-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Versiones ({snapshots.length})</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-1.5 font-medium">Version</th>
              <th className="px-2 py-1.5 font-medium">Fecha</th>
              <th className="px-2 py-1.5 font-medium">Cambio</th>
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s: any) => (
              <tr key={s.version} onClick={() => setSelectedVersion(s.version)}
                className={`cursor-pointer border-b border-border/20 transition-colors ${selectedVersion === s.version ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
                style={{ height: 36 }}>
                <td className="px-3 py-0 font-mono text-xs font-semibold text-foreground">v{String(s.version).padStart(3, '0')}</td>
                <td className="px-2 py-0 text-xs text-muted-foreground">{formatDate(s.created_at)}</td>
                <td className="px-2 py-0">
                  <Badge variant="default" className="text-[9px]">{CONFIG_TYPE_LABELS[s.config_type_changed] ?? s.config_type_changed}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {selectedVersion === null ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Selecciona una version para ver el detalle</p>
        ) : isLoadingDetail ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Cargando detalle...</p>
        ) : !detail ? (
          <p className="py-12 text-center text-sm text-muted-foreground">No se pudo cargar el detalle</p>
        ) : (
          <div>
            <div className="border-b border-border bg-muted/30 px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">Version v{String(detail.version).padStart(3, '0')}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDate(detail.created_at)} · {detail.created_by} · Cambio en {CONFIG_TYPE_LABELS[detail.config_type_changed] ?? detail.config_type_changed}
              </p>
              {detail.description && <p className="text-xs text-foreground mt-1">{detail.description}</p>}
            </div>
            <div className="p-4">
              {detail.config ? (
                <SnapshotConfigView config={detail.config} />
              ) : (
                <p className="py-4 text-center text-xs text-muted-foreground">Sin datos de configuracion en este snapshot</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
